import React from 'react'
import { Box, Link, TableColumn } from '@ally/metronome-ui'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import { Cell, Row } from 'react-table'
import { DashboardView } from '../../CustomizeDashboard/types'
import { AccountDetailsLink } from '../../Shared/AccountDetailsLink'
import { formatPercentage, getDashDash, isAccountClosed } from '../../utils'
import { STALE_DATA_COPY } from '../../constants'
import { TextBodyOverride } from '../../Shared/MetronomeOverrides'

interface CellProps {
  cell: Cell<Account>
  row: Row<Account>
}

interface GetColumnProps {
  isLgDown: boolean
  isWealthPartialOutage: boolean
  view: DashboardView
  hasStaleData: boolean
}

export const getColumns = ({
  isLgDown,
  isWealthPartialOutage,
  view,
  hasStaleData,
}: GetColumnProps): TableColumn<Account>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown
  const closedText = (
    <TextBodyOverride tag="span" size="sm" color="error">
      CLOSED
    </TextBodyOverride>
  )

  const columns = {
    accountNickname: {
      Header: 'Wealth Management',
      accessor: 'nickName',
      alignment: 'left',
      isSortable: true,
      Cell: ({ row }: { row: Row<Account> }): React.ReactNode | string => {
        return (
          <>
            <AccountDetailsLink
              account={row.original}
              text={row.original.nickName || row.original.name}
              allytm="wealthAccount"
            />
            <Box display="inline-block" aria-hidden="true">
              {maskAccountNumber(row.original.accountNumber)}
            </Box>
          </>
        )
      },
    },
    accountValueTotal: {
      Header: 'Account Value',
      accessor: 'value.total',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        const isClosed = isAccountClosed(row.original)
        if (!isClosed && value === 0) {
          return (
            <Link
              to="/ext-invest-live/transfer"
              text="Add money"
              withAllyTM="Wealth Add Money"
            />
          )
        }
        return isClosed ? closedText : asCurrency(value)
      },
    },
    netReturnPercent: {
      Header: 'Net Return %',
      accessor: 'value.netReturnPercent',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        const isClosed = isAccountClosed(row.original)

        if (isClosed) {
          return ''
        }

        return isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(value)
      },
    },
    yieldPercent: {
      Header: 'Yield %',
      accessor: 'value.yieldPercent',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { value }, row }: CellProps): React.ReactNode | string => {
        const isClosed = isAccountClosed(row.original)

        if (isClosed) {
          return ''
        }

        return isWealthPartialOutage || hasStaleData
          ? getDashDash(STALE_DATA_COPY)
          : formatPercentage(value)
      },
    },
    actions: {
      Header: '',
      accessor: 'actions',
      alignment: 'right',
      isSortable: false,
      Cell: (): React.ReactNode | string => '',
    },
  }

  const tileViewCols = [
    columns.accountNickname,
    columns.accountValueTotal,
    columns.netReturnPercent,
    columns.actions,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountNickname,
    columns.accountValueTotal,
    columns.netReturnPercent,
    columns.yieldPercent,
    columns.actions,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
